/**
 * Publishing, and taking it back down.
 *
 * Unpublishing is the privacy-critical half, so it follows one rule: the published state
 * comes from the SERVER at the moment of the action, never from the local project record.
 * The local record can be stale or absent — a different browser published it, the build
 * that published it wrote its result somewhere this device never saw — and trusting it is
 * how the phone ended up showing "Make private" on an app it then left public.
 *
 * The action therefore never silently skips. If the server's answer is unknown, it calls
 * BOTH endpoints rather than guessing: a page that stays up because we were unsure is the
 * one outcome that is not acceptable.
 */
import type { AppListing } from '@/backend/types';
import { ApiError, request } from './client';

export interface PublishedState {
  /** What the SERVER says, not what the project record remembers. */
  webPublished: boolean;
  /**
   * Whether the app appears in Discover. ABSENT means listed — every app published before
   * unlisting existed carries no flag, and reading absence as "private" would show the
   * whole existing catalogue as hidden.
   */
  listed: boolean;
  siteUrl?: string;
  /** Who can open a page that is up. ABSENT means public, exactly as the site handler serves it. */
  visibility?: 'public' | 'password';
  /** A moderator took the app down: its page is not served, and its owner cannot bring it back. */
  takenDown?: boolean;
  /** False when we could not find out; callers must then act, not skip. */
  known: boolean;
}

/** Ask the server whether this app is live. Never throws — an unknown answer is a state. */
export async function publishedState(appId: string): Promise<PublishedState> {
  if (!appId) return { webPublished: false, listed: true, known: false };
  try {
    // No retry: an unknown answer is already handled as "act anyway", so retrying would
    // only delay a takedown someone has asked for.
    const app = await request<AppListing & { listed?: boolean; takenDown?: boolean }>(
      `/apps/${encodeURIComponent(appId)}`,
      { retry: false },
    );
    return {
      webPublished: Boolean(app.webPublished),
      // Absent means listed. Only an explicit false is private.
      listed: app.listed !== false,
      siteUrl: app.siteUrl,
      ...(app.webVisibility ? { visibility: app.webVisibility } : {}),
      ...(app.takenDown || app.takenDownBy ? { takenDown: true } : {}),
      known: true,
    };
  } catch (err) {
    // A 404 is a real answer: there is no such app, so nothing is live.
    if (err instanceof ApiError && err.kind === 'not_found') {
      return { webPublished: false, listed: false, known: true };
    }
    /*
     * So is a 423. The detail route refuses a page that is up behind a password to anyone
     * without a site session — its owner included — and says so in exactly this status. Read
     * as "could not find out", every password page showed no status at all. `listed` is not in
     * a 423, so it takes the side absence always takes: listed, which a takedown then delists.
     */
    if (err instanceof ApiError && err.status === 423) {
      return { webPublished: true, listed: true, visibility: 'password', known: true };
    }
    return { webPublished: false, listed: true, known: false };
  }
}

export interface UnpublishResult {
  /** Endpoints actually called, so a caller can say what happened rather than guess. */
  calledWebUnpublish: boolean;
  calledDelist: boolean;
}

/**
 * Take a published app off the internet: both halves, in that order.
 *
 * `DELETE /apps/{id}/web` alone stops the page being SERVED but leaves the catalog entry,
 * so the app stays listed in Discover pointing at a dead link — which is not private. The
 * delist is the other half, and the owner's intent is "nobody can reach this", so the
 * action does both.
 *
 * Neither is skipped on an unknown state, and a failure of either is raised rather than
 * swallowed: the caller must NOT record this as done, because a local record saying
 * "private" over a page that is still up is the exact bug this exists to prevent.
 *
 * Permanent deletion of the app's data is deliberately NOT this. Submissions are kept.
 */
export async function makePrivate(appId: string): Promise<UnpublishResult> {
  if (!appId) {
    throw new ApiError('misconfigured', 'This app has no published id, so there is nothing to take down.');
  }

  const state = await publishedState(appId);
  // Unknown means act anyway. Skipping here is exactly the bug this exists to prevent.
  // Still delist a page that is already off: the catalog entry may linger in Discover.
  const pageOff = state.known && !state.webPublished;
  /*
   * ...unless the server says the app is not IN Discover. Every app built on the web is created
   * unlisted (`createApp` sends `listed: false`), so there is nothing to take off the shelf — and
   * delisting it anyway stamps it private (`unlistedAt`), after which the server will only put
   * the page back up through a relist, and a relist sets `listed: true`. "Take offline" and then
   * "Put it back online" moved somebody's own page into the public gallery. Only an explicit
   * `false` skips it: absent is listed, and unknown acts.
   */
  const offShelf = state.known && !state.listed;

  if (!pageOff) await request(`/apps/${encodeURIComponent(appId)}/web`, { method: 'DELETE' });
  if (!offShelf) await request(`/apps/${encodeURIComponent(appId)}`, { method: 'DELETE' });
  return { calledWebUnpublish: !pageOff, calledDelist: !offShelf };
}

/** What a creator is told when a moderator took the app down. Not a failure they can retry. */
export const TAKEN_DOWN_MESSAGE =
  'This app was taken down after a report. Contact support@saddle-sites.com if you think that was a mistake.';

export class TakenDownError extends Error {
  constructor() {
    super(TAKEN_DOWN_MESSAGE);
    this.name = 'TakenDownError';
  }
}

export interface RelistResult {
  appId: string;
  slug?: string;
  siteUrl?: string;
}

/**
 * Put a private app back in the catalogue, at the address it had before.
 *
 * Restores the LISTING only — `webPublished` stays false, so the page is still not served
 * until the content is republished through POST /apps/{id}/web. Two steps on purpose:
 * relisting is cheap and reversible, republishing regenerates the document. Callers that
 * offer one button must therefore do both, and say so.
 *
 * An app already listed answers 409, which is success from the caller's point of view.
 */
export async function relistApp(appId: string): Promise<RelistResult> {
  if (!appId) {
    throw new ApiError('misconfigured', 'This app has no id, so there is nothing to publish again.');
  }
  try {
    return await request<RelistResult>(`/apps/${encodeURIComponent(appId)}/relist`, { method: 'POST' });
  } catch (err) {
    if (err instanceof ApiError) {
      // A takedown is the one refusal a creator cannot fix by trying again.
      // `code` is the machine field; `reason` carries the human sentence on this route.
      if (err.kind === 'forbidden' && (err.code === 'taken_down' || err.takenDown)) {
        throw new TakenDownError();
      }
      if (err.kind === 'conflict') return { appId };
    }
    throw err;
  }
}
